import { useState } from "react";
import {
  Button,
  MenuItem,
  FormControl,
  Popover,
  Box,
  Checkbox,
  FormControlLabel,
  Chip,
  styled,
  TextField as MuiTextField
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';

import { IRules, IRulesOperation, MonthDayTypes, MonthPeriodTypes, MovementType, IPossibleMovement, OperationType, ICompetencyPeriodConfig } from "../../SetupLife.types";
import { Container, Row, Title, SelectForm, TextField, ButtonDelete } from "./Rules.styled";

// Styled components for the Editable Fields selector
const StyledEditableFieldsContainer = styled(Box)({
  border: '1px solid rgba(0, 0, 0, 0.23)',
  borderRadius: '8px',
  padding: '16px',
  minHeight: '56px',
  maxWidth: '300px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
  position: 'relative',
  '&:hover': {
    borderColor: 'rgb(121, 121, 121)',
  },
});

const StyledEditableFieldsLabel = styled('label')({
  position: 'absolute',
  top: '-8px',
  left: '12px',
  background: 'white',
  padding: '0 4px',
  fontSize: '14px',
  color: 'rgba(15, 15, 30, 0.6)',
});

const StyledChipsContainer = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  flex: 1,
});

const StyledEditableChip = styled(Chip)({
  backgroundColor: '#3024e1',
  color: 'white',
  '& .MuiChip-deleteIcon': {
    color: 'rgba(255, 255, 255, 0.7)',
    '&:hover': {
      color: 'white',
    },
  },
});

const StyledDropdownContainer = styled(Box)({
  padding: '8px',
  maxHeight: '200px',
  overflowY: 'auto',
});

const StyledSearchField = styled(MuiTextField)({
  marginBottom: '8px',
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderRadius: '8px',
    },
  },
});

// Interface for editable fields
interface EditableField {
  id: string;
  name: string;
}

// Mock data for editable fields - você pode ajustar conforme necessário
const mockEditableFields: EditableField[] = [
  { id: "name", name: "Nome completo" },
  { id: "birthDate", name: "Data de nascimento" },
  { id: "motherName", name: "Nome da mãe" },
  { id: "weddingDate", name: "Data de casamento" },
  { id: "type", name: "Tipo" },
  { id: "organizationId", name: "CNPJ da organização" },
];

interface IProps {
  rules: IRules;
  handleChange: (payload: Partial<IRules>) => void;
}

export function RulesTabOperation({ rules, handleChange }: IProps) {
  // State for editable fields dropdown
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const { possibleMovements = [] } = rules.rulesOperation;

  function handleChangeInvoicing(payload: Partial<IRulesOperation>) {
    const updatedOperationFields = { ...rules.rulesOperation, ...payload };

    handleChange({ ...rules, rulesOperation: updatedOperationFields });
  }

  // Get selected fields from rules
  const getSelectedFieldsFromRules = (): EditableField[] => {
    const editableFieldIds = rules.rulesOperation.campos || [];
    return mockEditableFields.filter(field => editableFieldIds.includes(field.id));
  };

  const selectedFields = getSelectedFieldsFromRules();

  // Helper functions for editable fields
  const handleOpenDropdown = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseDropdown = () => {
    setAnchorEl(null);
    setSearchTerm('');
  };

  const handleFieldToggle = (field: EditableField) => {
    const currentEditableFields = rules.rulesOperation.campos || [];
    const isSelected = currentEditableFields.includes(field.id);

    let newEditableFields: string[];
    if (isSelected) {
      newEditableFields = currentEditableFields.filter(id => id !== field.id);
    } else {
      newEditableFields = [...currentEditableFields, field.id];
    }

    handleChangeInvoicing({ campos: newEditableFields });
  };

  const handleRemoveField = (fieldId: string) => {
    const currentEditableFields = rules.rulesOperation.campos || [];
    const newEditableFields = currentEditableFields.filter(id => id !== fieldId);

    handleChangeInvoicing({ campos: newEditableFields });
  };

  /** Função responsável por adicionar nova linha vazia à array de possibleMovements. */
  function handleAddPossibleMovement() {
    const newMovement: IPossibleMovement = { operations: [], operatorCode: "" };

    handleChangeInvoicing({ possibleMovements: [...possibleMovements, newMovement] });
  }

  /** Função responsável por adicionar campos à array de possibleMovements individualmente. */
  function handleUpdatePossibleMovement(payload: Partial<IPossibleMovement>, index: number) {
    const updatedPayload = possibleMovements.map((m, i) => i === index ? { ...m, ...payload } : m);

    handleChangeInvoicing({ possibleMovements: updatedPayload });
  }

  /** Função responsável por remover campo da array de possibleMovements. */
  function handleRemovePossibleMovement(index: number) {
    const updatedPayload = possibleMovements.filter((_m, i) => i !== index);

    handleChangeInvoicing({ possibleMovements: updatedPayload });
  }

  function handleToggleCompetencyPeriod(enabled: boolean) {
    if (enabled) {
      handleChangeInvoicing({
        competencyPeriod: { periodStartDay: 1, periodEndDay: 'LAST_DAY', minimumActiveDays: 1, includeRetired: false, includeOnLeave: true }
      });
    } else {
      handleChangeInvoicing({ competencyPeriod: undefined });
    }
  }

  function handleChangeCompetencyPeriod(payload: Partial<ICompetencyPeriodConfig>) {
    handleChangeInvoicing({
      competencyPeriod: { ...rules.rulesOperation.competencyPeriod!, ...payload }
    });
  }

  /** Verifica se uma operação já foi selecionada em outra linha. */
  function isOperationUsed(operation: OperationType, currentIndex: number): boolean {
    return possibleMovements.some((movement, idx) =>
      idx !== currentIndex && movement.operations.includes(operation)
    );
  }

  const filteredFields = mockEditableFields.filter(field =>
    field.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const isDropdownOpen = Boolean(anchorEl);

  return (
    <Container>
      <Row>
        <SelectForm
          label="Tipo da movimentação"
          value={rules.rulesOperation.tipoDaMovimentacao}
          onChange={(e) => handleChangeInvoicing({ tipoDaMovimentacao: e.target.value as string })}
        >
          <MenuItem value={"ARQUIVO_MASSA_ATIVA"}>Arquivo massa ativa</MenuItem>
          <MenuItem value={"DELTA_ENTRE_DATAS_CORTE"}>Delta entre datas corte</MenuItem>
        </SelectForm>
        <SelectForm
          label="Incluir afastados"
          value={rules.rulesOperation.incluirAfastados}
          onChange={(e) => handleChangeInvoicing({ incluirAfastados: e.target.value as string })}
        >
          <MenuItem value={"SIM"}>Sim</MenuItem>
          <MenuItem value={"NAO"}>Nao</MenuItem>
        </SelectForm>
        <TextField
          label="Data corte"
          type="number"
          value={rules.rulesOperation.dataCorte ?? 0}
          onChange={(e) => handleChangeInvoicing({ dataCorte: String(e.target.value) ?? 0 })}
        />
        <SelectForm
          label="Data de geração do arquivo"
          value={rules.rulesOperation.dataGeracaoArquivo}
          onChange={(e) => handleChangeInvoicing({ dataGeracaoArquivo: e.target.value as string })}
        >
          <MenuItem value={"ULTIMO_DIA_MES"}>Último dia do mês de competência</MenuItem>
          <MenuItem value={"PRIMEIRO_DIA_MES"}>Primeiro dia do mês após competência</MenuItem>
        </SelectForm>
      </Row>
      <Title>Período de Competência</Title>
      <Row>
        <SelectForm
          label="Habilitar modelo snapshot"
          value={rules.rulesOperation.competencyPeriod ? "SIM" : "NAO"}
          onChange={(e) => handleToggleCompetencyPeriod(e.target.value === "SIM")}
        >
          <MenuItem value={"SIM"}>Sim</MenuItem>
          <MenuItem value={"NAO"}>Não</MenuItem>
        </SelectForm>
      </Row>
      {rules.rulesOperation.competencyPeriod && (
        <>
          <Row>
            <TextField
              label="Dia início do período"
              type="number"
              inputProps={{ min: 1, max: 28 }}
              value={rules.rulesOperation.competencyPeriod.periodStartDay}
              onChange={(e) => handleChangeCompetencyPeriod({ periodStartDay: Number(e.target.value) })}
            />
            <SelectForm
              label="Dia fim do período"
              value={rules.rulesOperation.competencyPeriod.periodEndDay}
              onChange={(e) => handleChangeCompetencyPeriod({ periodEndDay: e.target.value === 'LAST_DAY' ? 'LAST_DAY' : Number(e.target.value) })}
            >
              {Array.from({ length: 28 }, (_, i) => i + 1).map((day) => (
                <MenuItem key={day} value={day}>{day}</MenuItem>
              ))}
              <MenuItem value="LAST_DAY">Último dia do mês</MenuItem>
            </SelectForm>
          </Row>
          <Row>
            <TextField
              label="Dias mínimos ativos"
              type="number"
              inputProps={{ min: 1 }}
              value={rules.rulesOperation.competencyPeriod.minimumActiveDays}
              onChange={(e) => handleChangeCompetencyPeriod({ minimumActiveDays: Number(e.target.value) })}
            />
            <SelectForm
              label="Incluir aposentados"
              value={rules.rulesOperation.competencyPeriod.includeRetired ? "SIM" : "NAO"}
              onChange={(e) => handleChangeCompetencyPeriod({ includeRetired: e.target.value === "SIM" })}
            >
              <MenuItem value={"SIM"}>Sim</MenuItem>
              <MenuItem value={"NAO"}>Não</MenuItem>
            </SelectForm>
            <SelectForm
              label="Incluir afastados"
              value={rules.rulesOperation.competencyPeriod.includeOnLeave ? "SIM" : "NAO"}
              onChange={(e) => handleChangeCompetencyPeriod({ includeOnLeave: e.target.value === "SIM" })}
            >
              <MenuItem value={"SIM"}>Sim</MenuItem>
              <MenuItem value={"NAO"}>Não</MenuItem>
            </SelectForm>
          </Row>
        </>
      )}
      {rules.rulesOperation.tipoDaMovimentacao === "DELTA_ENTRE_DATAS_CORTE" && (
        <>
          <Title>Alteração</Title>
          <Row>
            <FormControl fullWidth>
              <StyledEditableFieldsContainer onClick={handleOpenDropdown}>
                <StyledEditableFieldsLabel>
                  Campos editáveis
                </StyledEditableFieldsLabel>
                <StyledChipsContainer>
                  {selectedFields.slice(0, 4).map((field) => (
                    <StyledEditableChip
                      key={field.id}
                      label={field.name}
                      onDelete={() => handleRemoveField(field.id)}
                      deleteIcon={<CloseIcon />}
                    />
                  ))}
                  {selectedFields.length > 4 && (
                    <StyledEditableChip
                      label={`+${selectedFields.length - 4}`}
                    />
                  )}
                </StyledChipsContainer>
                <ExpandMoreIcon />
              </StyledEditableFieldsContainer>

              <Popover
                open={isDropdownOpen}
                anchorEl={anchorEl}
                onClose={handleCloseDropdown}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                slotProps={{
                  paper: {
                    style: {
                      width: anchorEl?.clientWidth || 227,
                      maxHeight: 250,
                      borderRadius: '8px',
                      boxShadow: '0px 16px 32px -4px rgba(12,12,13,0.1), 0px 4px 4px -4px rgba(12,12,13,0.05)',
                    },
                  },
                }}
              >
                <StyledDropdownContainer>
                  <StyledSearchField
                    fullWidth
                    placeholder="Pesquisar campo"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size="small"
                  />
                  {filteredFields.map((field) => (
                    <FormControlLabel
                      key={field.id}
                      control={
                        <Checkbox
                          checked={(rules.rulesOperation.campos || []).includes(field.id)}
                          onChange={() => handleFieldToggle(field)}
                          color="primary"
                        />
                      }
                      label={field.name}
                      style={{
                        display: 'flex',
                        width: '100%',
                        margin: 0,
                      }}
                    />
                  ))}
                </StyledDropdownContainer>
              </Popover>
            </FormControl>
          </Row>
          <Title>Transferência</Title>
          <Row>
            <SelectForm
              label="Transferência"
              value={rules.rulesOperation.transferencia}
              onChange={(e) => handleChangeInvoicing({ transferencia: e.target.value as string })}
            >
              <MenuItem value={"SIM"}>Sim</MenuItem>
              <MenuItem value={"NAO"}>Nao</MenuItem>
            </SelectForm>
          </Row>
        </>
      )}
      <Title>Código Operadora (EB)</Title>
      {possibleMovements.map((row, index) => (
        <Row key={index} style={{ alignItems: "center" }}>
          <SelectForm
            label="Operação"
            multiple
            value={row.operations}
            onChange={(e) => handleUpdatePossibleMovement({ operations: e.target.value as OperationType[] }, index)}
          >
            <MenuItem value="inclusion" disabled={isOperationUsed("inclusion", index)}>
              Inclusão
            </MenuItem>
            <MenuItem value="exclusion" disabled={isOperationUsed("exclusion", index)}>
              Exclusão
            </MenuItem>
            <MenuItem value="change" disabled={isOperationUsed("change", index)}>
              Alterações
            </MenuItem>
            <MenuItem value="transfer" disabled={isOperationUsed("transfer", index)}>
              Transferências
            </MenuItem>
          </SelectForm>
          <TextField
            label="Código Operadora (EB)"
            value={row.operatorCode}
            onChange={(e) => handleUpdatePossibleMovement({ operatorCode: e.target.value }, index)}
          />
          <ButtonDelete color="error" onClick={() => handleRemovePossibleMovement(index)} />
        </Row>
      ))}
      <Button onClick={handleAddPossibleMovement}>+ Adicionar</Button>
    </Container>
  );
}
